import { createSlice, createAction, current, PayloadAction } from "@reduxjs/toolkit";
import { has, get, isArray, isNil } from "lodash";
import isNumeric from "@/common/lib/isNumeric";

// Trigger action for saga compatibility (saga watches this)
export const fetchOrganizationResponses = createAction<any>(
  "organizationResponses/trigger"
);

// External action this slice responds to
import { logoutStart } from "@/store/slices/auth";

// Top level:
// {
//    loading: 0, // qty of active requests (0 when none)
//    data: {}    // data object keyed by org ID
// }
//
// The data object will be keyed by org ID, with each entry being an object
// of responses (or nulls) keyed by criterion_id.

interface OrganizationResponsesState {
  loading?: number;
  data: Record<number, Record<number, any>>;
}

const initialState: OrganizationResponsesState = {
  data: {},
};

/**
 * Get the numeric value of a criterion_id parameter from an API request URL, if any.
 */
const extractCriterionIdFromUrl = (url: string): string | null => {
  const _url = new URL(url);
  const cId = _url.searchParams.get("criterion_id");
  if (cId && isNumeric(cId)) {
    return cId;
  }
  return null;
};

/**
 * Get the organization ID segment value from an API request URL.
 */
const extractOrganizationIdFromUrl = (url: string): number | null => {
  const _url = new URL(url);
  const path = _url.pathname;
  const regEx = /\/?api\/v1\/organizations\/([\d]+)\/responses\/?$/i;
  const result = regEx.exec(path);

  if (result && isArray(result) && result.length > 1 && isNumeric(result[1])) {
    return Number(result[1]);
  }
  return null;
};

const organizationResponsesSlice = createSlice({
  name: "organizationResponses",
  initialState,
  reducers: {
    fetchOrganizationResponsesStart(state, action: PayloadAction<any>) {
      const orgId = Number(action.payload.organization_id);

      // Add org entry if not yet present.
      if (!has(state.data, orgId)) {
        state.data[orgId] = {};
      }

      // Add/increment loading flag (top-level, not org-specific).
      if (state.loading === undefined) {
        state.loading = 1;
      } else {
        state.loading++;
      }

      // If request was for a specific criterion, create or populate the
      // necessary properties to reflect that.
      if (
        has(action.payload, "criterion_id") &&
        isNumeric(action.payload.criterion_id)
      ) {
        state.data[orgId][action.payload.criterion_id] = {
          loading: true,
          failed: false,
          response: null,
        };
      }
    },
    fetchOrganizationResponsesSuccess(state, action: PayloadAction<any>) {
      if (!isArray(action.payload.data)) {
        console.error(
          "Invalid payload.data in fetchOrganizationResponsesSuccess.",
          action.payload.data
        );
        return;
      }

      let orgId: number | null = null;
      let criterionId: string | null = null;
      const newOrgProps: Record<string, any> = {};

      // Handle payloads with at least one response.
      if (action.payload.data.length > 0) {
        orgId = action.payload.data[0].organization_id;
        for (let i = 0; i < action.payload.data.length; i++) {
          newOrgProps[action.payload.data[i].criterion_id] = {
            failed: false,
            loading: false,
            response: action.payload.data[i],
          };
        }
      }
      // Handle payloads with _no_ responses.
      else {
        if (has(action.payload, "links.first")) {
          criterionId = extractCriterionIdFromUrl(action.payload.links.first);
          orgId = extractOrganizationIdFromUrl(action.payload.links.first);
          if (criterionId && orgId) {
            newOrgProps[criterionId] = {
              failed: false,
              loading: false,
              response: null,
            };
          }
        }
      }

      let oldOrgProps: any = {};
      if (!isNil(orgId)) {
        oldOrgProps = get(state.data, orgId, null);
      }

      state.loading = (state.loading || 1) - 1;
      if (orgId !== null) {
        state.data[orgId] = {
          ...oldOrgProps,
          ...newOrgProps,
        };
      }
    },
    fetchOrganizationResponsesFailure(state, action: PayloadAction<any>) {
      console.error("fetchOrganizationResponsesFailure", current(state), action.payload);

      // Try to extract org and criterion info from URLs included in
      // returned payload.
      let oId: number | null = null;
      let cId: string | null = null;
      if (has(action.payload, "links.first")) {
        cId = extractCriterionIdFromUrl(action.payload.links.first);
        oId = extractOrganizationIdFromUrl(action.payload.links.first);
      }

      // We only do this if it was a single-criterion request and we
      // were able to extract that info.
      if (oId && cId) {
        const oldOrgEntry = get(state.data, oId, {});
        state.data[oId] = {
          ...oldOrgEntry,
          [cId]: {
            failed: true,
            loading: false,
            response: null,
          },
        };
      }

      state.loading = (state.loading || 1) - 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutStart, () => ({ ...initialState }));
  },
});

export const {
  fetchOrganizationResponsesStart,
  fetchOrganizationResponsesSuccess,
  fetchOrganizationResponsesFailure,
} = organizationResponsesSlice.actions;

export default organizationResponsesSlice.reducer;
